import { ZodError, type ZodType } from "zod";

/**
 * The single place an API decides what an unhandled throw becomes.
 *
 * Milestone 1 fixed this class of defect **by hand, one site at a time**: ~40
 * routes leaking provider text, 14 answering 500 where a 4xx belonged, several
 * calling `.parse(await c.req.json())` outside a `try`. Every one of those was a
 * route author having to remember something, and forty corrected sites are forty
 * chances for the forty-first to be written next week.
 *
 * Both apps had an `onError` and **neither classified anything**: web's
 * `apiErrorHandler` and `apps/api`'s `handleRouteError` each returned a blanket
 * 500. A validation failure that escaped a handler was therefore reported as a
 * server fault — the caller told nothing useful, and the log told us nothing
 * either. No route does that today, because M1 wrapped them all; this exists so
 * the next one is safe without anyone remembering to.
 *
 * ## What it does NOT do
 *
 * It never puts an upstream message in a response. A `ZodError` is our own
 * schema's output and is safe to describe; anything else is unknown provenance —
 * a provider SDK, a database driver — and gets a fixed string. That asymmetry is
 * the whole point, and it is why this takes `unknown` rather than `Error`.
 */

/** Field paths and messages from our own schema. Never a submitted value. */
export interface RouteValidationIssue {
  path: string;
  message: string;
}

export interface ClassifiedRouteError {
  status: 400 | 404 | 409 | 500 | 503;
  error: string;
  /** A stable machine-readable reason, for a `ConflictError`. */
  code?: string;
  issues?: RouteValidationIssue[];
}

/**
 * Classifies an unhandled route error.
 *
 * @param err whatever reached `onError` — genuinely `unknown`, since a thrown
 *   non-Error is legal and does happen (a rejected string from a provider SDK).
 */
/**
 * Postgres codes for "this is not a value at all".
 *
 * - `22P02` invalid_text_representation — cannot be cast to the column's type,
 *   which in this schema is almost always a non-uuid id.
 * - `22021` character_not_in_repertoire — a NUL byte in text. Postgres cannot
 *   store one in any text column, so it can never be a legitimate value from
 *   any caller; it is the same class as the cast failure and was found by the
 *   same sweep, on 104 routes, as the one payload still answering 500 after the
 *   cast case was fixed.
 *
 * Both mean the caller sent something that cannot name or fill anything. What is
 * deliberately NOT here is anything that says something about OUR state — a
 * unique violation (`23505`), a deadlock (`40P01`), a NOT NULL violation
 * (`23502`), a lost connection. Those stay 500, because reporting a real outage
 * as "not found" is worse than the noise it would hide.
 */
const NOT_A_VALUE_CODES = new Set(["22P02", "22021"]);

/** Walks the `cause` chain for a Postgres error code. Bounded, since it is a chain. */
function postgresErrorCode(err: unknown, depth = 0): string | null {
  if (depth > 8 || err === null || typeof err !== "object") return null;
  const code = (err as { code?: unknown }).code;
  if (typeof code === "string") return code;
  return postgresErrorCode((err as { cause?: unknown }).cause, depth + 1);
}

/**
 * Marks a throw as OUR fault, whatever it looks like.
 *
 * A `ZodError` alone cannot say which direction the parse went, and the two
 * directions mean opposite things: `taskInputSchema.parse(body)` failing is the
 * caller's mistake (400), while `TaskDTO.parse(row)` failing means a row no
 * longer matches the shape we promise — our bug, and a 500.
 *
 * Read BEFORE the `ZodError` branch below, so a marked error never takes the
 * 400 path. Set as a plain own property rather than held in a module-level
 * `WeakSet`, because the error crosses a package boundary on its way to
 * `onError` and a set would only work while both sides share one module
 * instance.
 */
const SERVER_FAULT = "sukhaServerFault" as const;

function isServerFault(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as Record<string, unknown>)[SERVER_FAULT] === true
  );
}

/**
 * Parses something WE produced — a database row, an upstream payload — against
 * the schema we promise for it, and marks a failure as a server fault.
 *
 * Use this for every `*DTO.parse(...)`; use a bare `.parse()` for caller input,
 * where a 400 with issues is exactly right.
 *
 * Why it exists: while every route handler caught its own errors and returned a
 * blanket 500, the classifier never saw a response-side `ZodError`. Removing
 * 233 of those catch blocks made the default reachable — and the default was
 * 400, which tells the caller to fix a request they got right and, because
 * `isCallerFault` gates the Sentry report, keeps a real server fault out of
 * Sentry. A silent failure is worse than the noisy one it replaced.
 */
export function parseResponse<T>(schema: ZodType<T>, value: unknown): T {
  try {
    return schema.parse(value);
  } catch (error) {
    if (error instanceof ZodError) {
      Object.defineProperty(error, SERVER_FAULT, {
        value: true,
        enumerable: false,
      });
    }
    throw error;
  }
}

/**
 * Marks a throw as "the thing the caller named does not exist".
 *
 * A property rather than `instanceof`, for the same reason as `SERVER_FAULT`:
 * the error is thrown in `@sukha/db` and read here, and `instanceof` only holds
 * while both sides share one module instance.
 */
const NOT_FOUND = "sukhaNotFound" as const;

/**
 * Base class for a query-layer "no such resource" — a task, a member, a chat
 * message — that should answer 404 wherever it surfaces.
 *
 * Before this, each was a plain `Error` (or a class with no shared meaning), so
 * a handler that let it propagate answered **500**, and one that caught it had
 * to remember an `instanceof` branch. `GET /v1/chat/user/:userId` for an id
 * naming nobody answered 500; so did completing a task that had just been
 * deleted. Extending this puts the decision in `classifyRouteError`, where
 * `onError` and every catch prologue already look.
 *
 * Per `member-resource-404.mdc` the response never says WHICH thing was
 * missing, or why: "someone else's" and "does not exist" must be identical.
 */
export class NotFoundError extends Error {
  readonly [NOT_FOUND] = true;

  constructor(message: string) {
    super(message);
    this.name = "NotFoundError";
  }
}

/** Marks a throw as "the caller's request is one we refuse", with our own words. */
const BAD_REQUEST = "sukhaBadRequest" as const;

/**
 * A domain refusal of the caller's request — a file type this upload may not
 * have, a size over the limit — that answers 400 wherever it surfaces.
 *
 * Unlike every other branch of `classifyRouteError`, its MESSAGE is the answer:
 * the caller needs to know which rule they broke, and the text is a fixed
 * string written by us at the throw site. Never construct one from an upstream
 * message or a submitted value.
 */
export class BadRequestError extends Error {
  readonly [BAD_REQUEST] = true;

  constructor(message: string) {
    super(message);
    this.name = "BadRequestError";
  }
}

function isBadRequest(err: unknown): err is Error {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as Record<string, unknown>)[BAD_REQUEST] === true
  );
}

/** Marks a throw as "refused because of the state the caller is in". */
const CONFLICT = "sukhaConflict" as const;

/**
 * A refusal because of the caller's own current state — This Session already
 * holds its three tasks — that answers 409 wherever it surfaces (TX-5426).
 *
 * Like `BadRequestError`, its MESSAGE is the answer, written by us at the
 * throw site; `code` is the stable reason a client switches on to word its
 * own message. Never construct one from an upstream message or a submitted
 * value. Only for the CALLER's state: per `member-resource-404.mdc`, someone
 * else's resource is a 404, never a 409.
 */
export class ConflictError extends Error {
  readonly [CONFLICT] = true;

  constructor(
    message: string,
    readonly code: string
  ) {
    super(message);
    this.name = "ConflictError";
  }
}

function isConflict(err: unknown): err is ConflictError {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as Record<string, unknown>)[CONFLICT] === true
  );
}

/** Marks a throw as "something we depend on did not answer; try again". */
const SERVICE_UNAVAILABLE = "sukhaServiceUnavailable" as const;

/**
 * An outage of something we depend on — storage that timed out or answered
 * 5xx — that answers 503 wherever it surfaces (TX-5299).
 *
 * Ours, so it is logged at `error` and reported. It exists so an outage is
 * never answered as "not found": that told a member their file was gone. The
 * response never carries the message or the cause; keep the upstream error as
 * `cause` for the log.
 */
export class ServiceUnavailableError extends Error {
  readonly [SERVICE_UNAVAILABLE] = true;

  constructor(message: string, options?: { cause?: unknown }) {
    super(message);
    this.name = "ServiceUnavailableError";
    // Set by hand: this package's lib predates `ErrorOptions`.
    if (options && "cause" in options) {
      Object.defineProperty(this, "cause", { value: options.cause, enumerable: false, writable: true, configurable: true });
    }
  }
}

function isServiceUnavailable(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as Record<string, unknown>)[SERVICE_UNAVAILABLE] === true
  );
}

/** Whether `err` is a `NotFoundError` (by marker, so across module instances). */
export function isNotFound(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as Record<string, unknown>)[NOT_FOUND] === true
  );
}

/**
 * The runtime's wording when `Request.formData()` is handed a body it cannot
 * read as a form: the wrong `Content-Type`, or a multipart body that does not
 * parse. Matched on the TYPE plus these exact phrases, never on a loose word,
 * so an unrelated `TypeError` of ours still reports as a 500.
 *
 * Node's two phrasings, then Bun's: `apps/api/dev.ts` runs under Bun, and the
 * same JSON-to-a-form-route request answered 500 there (TX-5081).
 */
const FORM_BODY_ERROR =
  /^Content-Type was not one of "multipart\/form-data" or "application\/x-www-form-urlencoded"|^Failed to parse body as FormData|^Can't decode form data from body because of incorrect MIME type\/boundary/;

/**
 * Whether a foreign-key violation is about the `users` table.
 *
 * Drizzle wraps the driver error, so the constraint is looked for through the
 * `cause` chain the same way the code is. Postgres names these
 * `<table>_<column>_users_id_fk`, and only that suffix counts — a constraint
 * merely mentioning users elsewhere in its name does not.
 */
function foreignKeyTargetsUsers(err: unknown, depth = 0): boolean {
  if (depth > 8 || err === null || typeof err !== "object") return false;
  const constraint = (err as { constraint?: unknown }).constraint;
  if (typeof constraint === "string") return /_users_id_fk$/.test(constraint);
  return foreignKeyTargetsUsers((err as { cause?: unknown }).cause, depth + 1);
}

/**
 * Whether a write failed because the member it names has no `users` row.
 *
 * A foreign-key violation (`23503`) on a `*_users_id_fk` constraint, found
 * through Drizzle's `cause` chain. Reached whenever a signed-in member's first
 * requests land before `POST /v1/user/create` has made their row — an expected
 * state that answers 404 below, not an outage, so a caller may log it quietly.
 */
export function isMissingMemberRow(err: unknown): boolean {
  return postgresErrorCode(err) === "23503" && foreignKeyTargetsUsers(err);
}

export function classifyRouteError(err: unknown): ClassifiedRouteError {
  /*
   * Ours, and said so at the parse site. No `issues`: the 400 path describes
   * our schema to the caller who sent the bad data, and here the bad data is
   * ours — the field paths of an internal row are not a caller's business.
   */
  if (isServerFault(err)) {
    return { status: 500, error: "Internal server error" };
  }

  // An outage, not a missing thing (see `ServiceUnavailableError`).
  if (isServiceUnavailable(err)) {
    return { status: 503, error: "Service unavailable" };
  }

  // Our own refusal, in our own words (see `BadRequestError`).
  if (isBadRequest(err)) {
    return { status: 400, error: err.message };
  }

  // Refused because of the caller's own state (see `ConflictError`).
  if (isConflict(err)) {
    return { status: 409, error: err.message, code: err.code };
  }

  if (err instanceof ZodError) {
    return {
      status: 400,
      error: "Invalid request",
      /*
       * `path` and `message` only. `issue.input` and the received value are
       * deliberately dropped: a validation error on a password or token field
       * would otherwise echo the submitted value straight back into a response
       * body — and into any log that records it.
       */
      issues: err.issues.map((issue) => ({
        path: issue.path.join("."),
        message: issue.message,
      })),
    };
  }

  /*
   * A malformed id is the caller's mistake, and it answers like an absent one.
   *
   * 149 routes used to answer 500 for a path parameter that is not a uuid: the
   * value arrives as a BOUND parameter (so this is not injection — the query
   * builder did its job), Postgres refuses the cast, and the driver's rejection
   * reaches the handler's catch as an unexpected error.
   *
   * 404 rather than 400, deliberately. `member-resource-404.mdc` requires that
   * an id which cannot name anything answer exactly as one that names nothing
   * does — any difference between the two is an enumeration oracle, which is the
   * whole reason that rule exists. The response says nothing about WHY.
   *
   * Scoped to this one code on purpose. A unique violation or a deadlock is ours
   * and must stay a 500; turning the whole class into 404 would hide real
   * outages behind a status that says the resource simply is not there.
   *
   * Drizzle wraps the driver error as `Failed query: …` with the original as
   * `cause`, so the code is looked for through the chain rather than on the
   * object it was handed.
   */
  const code = postgresErrorCode(err);
  if (code !== null && NOT_A_VALUE_CODES.has(code)) {
    return { status: 404, error: "Not found" };
  }

  // A typed "no such resource" from the query layer. Same answer, same body, as
  // the malformed id above: the two must be indistinguishable.
  if (isNotFound(err)) {
    return { status: 404, error: "Not found" };
  }

  /*
   * A write whose MEMBER does not exist answers 404, like the read already does.
   *
   * A valid session whose `users` row is missing made every write fail on
   * `tasks_user_id_users_id_fk` and answer "Internal server error"
   * (THE-SUKHA-COMPANY-SJ). Reachable in production whenever the `user.created`
   * webhook is delayed or dropped and the client has not yet called
   * `POST /v1/user/create`.
   *
   * **Not healed, answered.** The first attempt created the row on demand, and
   * the contract suite showed what that really did: the same state is reached by
   * DELETING an account, so re-creating the row resurrects it. A missing row is
   * genuinely ambiguous between "not provisioned yet" and "deliberately
   * removed", and this layer cannot tell them apart — so it must not guess.
   *
   * 404 is the answer `GET /v1/user/me` already gives a caller with no row, and
   * what `member-resource-404.mdc` requires: an id that cannot name anything
   * answers exactly as one that names nothing.
   *
   * Matched on the CONSTRAINT, not the code alone. `23503` on any other table —
   * a task pointing at a missing category — is our bug and stays a 500.
   */
  if (isMissingMemberRow(err)) {
    return { status: 404, error: "Not found" };
  }

  /*
   * Two body shapes that cannot be anything but the caller's doing.
   *
   * The fuzz sweep records routes that answer 5xx to a hostile BODY on a valid
   * path. Most of those causes say something about OUR state and must stay 500.
   * These two cannot:
   *
   * - `SyntaxError` from `JSON.parse` — the body is not JSON. It is thrown
   *   before any handler logic runs, so there is no server-side path to it.
   * - Drizzle's "No values to set" — `.set({})`, reachable only by sending an
   *   empty or entirely-unknown patch body.
   *
   * Deliberately NOT here: Postgres `23502` (NOT NULL violation). It looks like
   * the same class, and it fires just as readily when WE forget to set a column.
   * A 400 there would file our own bug under the one status nobody investigates.
   *
   * The `SyntaxError` check is on the TYPE plus the parser's own wording, not on
   * the word "json" appearing in a message — a provider failure that mentions
   * json must not be downgraded to 400 and hidden from Sentry.
   */
  if (
    err instanceof SyntaxError &&
    /JSON|Unexpected token|Unexpected end of/i.test(err.message)
  ) {
    /*
     * Named, not generic.
     *
     * "Invalid request" tells a caller who sent `{not json` nothing they can
     * act on — they have to guess whether it was the shape, a field, the
     * method or the body. "Invalid JSON body" is the one fact that fixes it,
     * and it describes THEIR input, so it leaks nothing about us.
     *
     * The parser's own message is deliberately not forwarded: it carries an
     * offset into a payload we do not want to quote back.
     */
    return { status: 400, error: "Invalid JSON body" };
  }
  if (err instanceof TypeError && FORM_BODY_ERROR.test(err.message)) {
    // A form route handed JSON, or a multipart body that does not parse. The
    // chat send routes answered 500 for exactly this.
    return { status: 400, error: "Invalid form body" };
  }
  if (err instanceof Error && err.message === "No values to set") {
    // Same reasoning: say which mistake it was. An empty patch body is a
    // different fix from a malformed one.
    return { status: 400, error: "No fields to update" };
  }

  return { status: 500, error: "Internal server error" };
}

/**
 * Whether a classified error is the caller's fault.
 *
 * Exposed so a logger can pick its level without re-deriving the rule: a 400 is
 * a client mistake and belongs at `warn`, while a 500 is ours and belongs at
 * `error` with a Sentry report. Two places deciding that independently is how
 * they drift.
 */
export function isCallerFault(classified: ClassifiedRouteError): boolean {
  return classified.status < 500;
}
